'use client';

import React, { useMemo } from 'react';
import { DataTable, SectionHeader, Flex } from '@highereducation/hen-ui';
import type { ColumnDef, SortingFn } from '@tanstack/react-table';

export interface CellData {
  text: string;
  content: React.ReactNode;
}

type RowData = Record<string, CellData>;

export interface SortableTableClientProps {
  title?: string;
  headers: string[];
  rows: CellData[][];
  pageSize?: number;
  showSearch?: boolean;
}

/**
 * Strips currency symbols, commas, percent signs, and whitespace so the
 * remaining string can be parsed as a plain number.
 */
function parseNumeric(s: string): number | null {
  const cleaned = s.replace(/[$,%\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function detectNumericColumn(data: RowData[], header: string): boolean {
  let hasValue = false;
  for (const row of data) {
    const text = row[header]?.text ?? '';
    if (text.trim() === '') continue;
    hasValue = true;
    if (parseNumeric(text) === null) return false;
  }
  return hasValue;
}

const numericSortFn: SortingFn<RowData> = (rowA, rowB, columnId) => {
  const a = parseNumeric(String(rowA.getValue(columnId))) ?? 0;
  const b = parseNumeric(String(rowB.getValue(columnId))) ?? 0;
  return a - b;
};

export function SortableTableClient({
  title,
  headers,
  rows,
  pageSize = 10,
  showSearch = false,
}: SortableTableClientProps) {
  const data: RowData[] = useMemo(
    () =>
      rows.map((row) =>
        headers.reduce((obj, header, index) => {
          obj[header] = row[index] ?? { text: '', content: '' };
          return obj;
        }, {} as RowData),
      ),
    [headers, rows],
  );

  const columns: ColumnDef<RowData, string>[] = useMemo(
    () =>
      headers.map((header) => {
        const isNumeric = detectNumericColumn(data, header);
        return {
          id: header,
          accessorFn: (row: RowData) => row[header]?.text ?? '',
          header: header,
          enableSorting: true,
          ...(isNumeric && { sortingFn: numericSortFn }),
          cell: ({ row }: { row: { original: RowData } }) =>
            row.original[header]?.content ?? '',
        };
      }),
    [headers, data],
  );

  if (columns.length === 0) return null;

  return (
    <Flex direction='column' gap='md'>
      {title && <SectionHeader size='h3' title={title} />}
      <DataTable
        columns={columns}
        data={data}
        mode='paginated'
        pageSize={pageSize}
        searchColumns={showSearch ? headers : undefined}
        {...(title ? { 'aria-label': title } : {})}
      />
    </Flex>
  );
}
